import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useDatabaseCourseNavigation } from './useDatabaseCourseNavigation';
import { UnifiedProgressService } from '@/services/unifiedProgressService';

interface DashboardStats {
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  notStartedCourses: number;
  overallProgress: number;
  certificateCount: number;
  learningStreak: number;
  loading: boolean;
}

export const useRealDashboardStats = (): DashboardStats => {
  const { user, userRole } = useSupabaseAuth();
  const { courses: dbCourses } = useDatabaseCourseNavigation();
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    notStartedCourses: 0,
    overallProgress: 0,
    certificateCount: 0,
    learningStreak: 0,
    loading: true
  });

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    }
  }, [user, userRole]);

  // Real-time progress updates listener
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('progress_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_module_progress',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          console.log('Progress updated, refreshing stats');
          fetchDashboardStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchDashboardStats = async () => {
    if (!user) return;

    try {
      setStats(prev => ({ ...prev, loading: true }));

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Dashboard stats timeout')), 10000);
      });

      // Get assignments with timeout protection
      const assignmentsPromise = supabase
        .from('course_assignments')
        .select('id, status, assignment_status, course_id')
        .eq('user_id', user.id)
        .in('assignment_status', ['assigned'])
        .in('status', ['active', 'completed'])
        .limit(200); // Prevent excessive data loading

      const assignmentsResult = await Promise.race([assignmentsPromise, timeoutPromise]) as any;
      const assignments = assignmentsResult?.data || [];

      const totalCourses = assignments?.length || 0;
      const completedCourses = assignments?.filter(a => a.status === 'completed').length || 0;

      // Batch progress queries with concurrency limit (max 5 parallel)
      const progressResults = [];
      const batchSize = 5;
      
      for (let i = 0; i < assignments.length; i += batchSize) {
        const batch = assignments.slice(i, i + batchSize);
        const batchPromises = batch.map(async (assignment) => {
          if (assignment.status === 'completed') return { progress: 100, hasStarted: true };
          
          try {
            const summary = await UnifiedProgressService.getCourseProgress(user.id, assignment.course_id);
            return { 
              progress: summary.overallProgress, 
              hasStarted: summary.completedModules > 0 || summary.overallProgress > 0 
            };
          } catch (error) {
            console.warn(`Failed to fetch progress for ${assignment.course_id}:`, error);
            return { progress: 0, hasStarted: false };
          }
        });
        
        const batchResults = await Promise.allSettled(batchPromises);
        progressResults.push(...batchResults.map(r => r.status === 'fulfilled' ? r.value : { progress: 0, hasStarted: false }));
      }
      
      const inProgressCourses = progressResults.filter(r => r.hasStarted && r.progress < 100).length;
      const notStartedCourses = progressResults.filter(r => !r.hasStarted).length;
      const totalProgressPoints = progressResults.reduce((sum, r) => sum + r.progress, 0);
      const overallProgress = totalCourses > 0 ? Math.round(totalProgressPoints / totalCourses) : 0;

      // Fetch certificate count
      const { data: certificates } = await supabase
        .from('certificates')
        .select('id')
        .eq('user_id', user.id);

      const certificateCount = certificates?.length || 0;

      // Calculate learning streak
      const { data: activityLogs } = await supabase
        .from('module_activity_logs')
        .select('created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      const learningStreak = calculateLearningStreak(activityLogs || []);

      const allCoursesUnifiedCount = dbCourses.length;
      const effectiveTotalCourses = userRole === 'admin' ? allCoursesUnifiedCount : totalCourses;

      setStats({
        totalCourses: effectiveTotalCourses,
        completedCourses,
        inProgressCourses,
        notStartedCourses,
        overallProgress,
        certificateCount,
        learningStreak,
        loading: false
      });

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const calculateLearningStreak = (activityLogs: { created_at: string }[]) => {
    if (!activityLogs.length) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const uniqueDays = new Set<string>();
    activityLogs.forEach(log => {
      const logDate = new Date(log.created_at);
      logDate.setHours(0, 0, 0, 0);
      uniqueDays.add(logDate.toDateString());
    });

    const sortedDays = Array.from(uniqueDays)
      .map(day => new Date(day))
      .sort((a, b) => b.getTime() - a.getTime());

    let streak = 0;
    let currentDate = new Date(today);

    for (const day of sortedDays) {
      if (day.getTime() === currentDate.getTime()) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else if (day.getTime() < currentDate.getTime()) {
        break;
      }
    }

    return streak;
  };

  return stats;
};